/**
 * <Logo> — la marque Masteur (atom).
 *
 * Deux dessins, ceux du livre V16 : le **lockup** (macaron + « Masteur » cursif), porté par le
 * header en grand écran, et le **macaron** seul, qui prend sa place quand la largeur manque.
 * Rien d'autre : pas de version texte, pas de couleur alternative — le logo ne se recompose pas.
 * Le M est **blanc** et le ruban `#FFE000` : le crème de la V10 est sorti de la palette.
 *
 * Le fichier est un PNG empaqueté — les PNG OFFICIELS du kit de marque (`logo/png/*-h480.png`),
 * ramenés à 144 px (macaron) et 192 px (lockup) de haut. Ne pas les re-rastériser depuis un SVG
 * de travail : le m du macaron a changé en V16 (queue jusqu'au bord du disque), et seul le kit
 * le porte. Metro ne sait pas charger un `.svg` sans transformateur, et en ajouter un pour deux
 * images serait une dépendance de plus.
 *
 * **Le lockup a deux dessins, le macaron un seul.** Le mot « Masteur » est marine : sur la nuit
 * il s'effacerait, d'où `logo-lockup-nuit.png`, où il est blanc. Le macaron, lui, porte son M
 * blanc sur une tuile marine — il tient des deux côtés sans changer. C'est la seule chose du
 * design system qu'un jeton ne peut pas porter : un dessin ne se repointe pas.
 *
 * L'appelant donne la HAUTEUR ; la largeur suit le rapport du dessin, qui est intrinsèque au
 * fichier — ce n'est pas une décision de mise en page qu'un jeton porterait.
 */
import React from "react"
import { Image } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import lockup from "../../../../assets/brand/logo-lockup.png"
import lockupNight from "../../../../assets/brand/logo-lockup-nuit.png"
import macaron from "../../../../assets/brand/logo-macaron.png"
import { useIsDarkTheme } from "../../hooks/useIsDarkTheme"

/** Rapport largeur / hauteur de chaque dessin — celui des PNG du kit (359 × 480 et 1056 × 480). */
const RATIO = { lockup: 1056 / 480, macaron: 359 / 480 } as const

const SOURCES = { lockup, macaron } as const
const NIGHT_SOURCES = { lockup: lockupNight, macaron } as const

export type LogoVariant = keyof typeof SOURCES

export type LogoProps = {
    /** `lockup` (macaron + mot) ou `macaron` seul — `lockup` par défaut. */
    variant?: LogoVariant
    /** Hauteur en px — 40 par défaut. */
    height?: number
    testID?: string
}

export function Logo(props: LogoProps) {
    const { variant = "lockup", height = 40, testID } = props
    const sources = useIsDarkTheme() ? NIGHT_SOURCES : SOURCES

    return (
        <Image
            testID={testID}
            source={sources[variant]}
            style={styles.logo(height, Math.round(height * RATIO[variant]))}
            accessibilityLabel="Masteur"
            resizeMode="contain"
        />
    )
}

const styles = StyleSheet.create(() => ({
    logo: (height: number, width: number) => ({ height, width }),
}))
